import React, { FunctionComponent, useCallback, useContext, useRef } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { Dimensions, View } from 'react-native'

import BottomSheetGorhom from '@gorhom/bottom-sheet'
import { isEmpty } from 'lodash-es'
import styled, { useTheme } from 'styled-components/native'

import {
  CostMessagingContentType,
  CostMessagingModalContent,
  CostMessagingModalCoverageInfo,
  CoverageType,
  EAP_MEDS_CONTENT_TYPE,
} from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/constants'
import { CustomerName } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/customer'
import { isUnlimitedSessionLimit } from '@lyrahealth-inc/shared-app-logic/src/features/common/utils/sessionLimit'
import { ProgramNames } from '@lyrahealth-inc/shared-app-logic/src/models/programs/Programs'
import {
  SessionCountByProgram,
  SessionLimitByProgram,
} from '@lyrahealth-inc/shared-app-logic/src/models/session/SessionUsage'

import { ActivityBadge } from '../../atoms/activityBadge/ActivityBadge'
import { BodyText } from '../../atoms/bodyText/BodyText'
import { CloseIcon } from '../../atoms/icons/CloseIcon'
import { IllustrationBadge } from '../../atoms/illustrationBadge/IllustrationBadge'
import { ChildTherapySecondaryRec } from '../../atoms/illustrations/ChildTherapySecondaryRec'
import { CoachingSecondaryRec } from '../../atoms/illustrations/CoachingSecondaryRec'
import { ControlledMedsSecondaryRec } from '../../atoms/illustrations/ControlledMedsSecondaryRec'
import { ProfileBookingShortTermDisabilityEvaluationIllustration } from '../../atoms/illustrations/ProfileBookingShortTermDisabilityEvaluationIllustration'
import { TherapySecondaryRec } from '../../atoms/illustrations/TherapySecondaryRec'
import { PressableOpacity } from '../../atoms/pressableOpacity/PressableOpacity'
import { PrimaryButton } from '../../atoms/primaryButton/PrimaryButton'
import { Subhead } from '../../atoms/subhead/Subhead'
import { TertiaryButton } from '../../atoms/tertiaryButton/TertiaryButton'
import { BadgeShape, BadgeVariant } from '../../badge/constants'
import { AppContext } from '../../context'
import { useMediaQuerySize } from '../../hooks/useMediaQuerySize'
import { Modal } from '../../organisms/modal/Modal'
import { BodyTextSize, SubheadSize } from '../../styles/typeStyles'
import { ThemeType } from '../../utils/themes/ThemeProvider'
import { tID } from '../../utils/utils'

export interface CoverageMessagingModalProps {
  setIsModalOpen: (isModalOpen: boolean) => void
  isModalOpen: boolean
  coverageModalTitleText?: React.ReactNode
  coverageModalContents: CostMessagingModalContent[]
  costMessagingModalCoverageInfo?: CostMessagingModalCoverageInfo
  trackCoverageModalPress: (isOpen: boolean) => void
  customerName?: string
  numSessions?: string
  numberOfSessionByProgram?: SessionLimitByProgram
  displayVisitsPerIssuePerYear?: number
  displayVisitsPerIssuePerYearByProgram?: SessionCountByProgram
  subheaderText?: string
  goToSearchForCare?: () => void
}

const ContentContainer = styled.View<{ theme: ThemeType; isMobileSized: boolean }>(({ theme, isMobileSized }) => ({
  padding: theme.breakpoints.isMobileSized ? `${theme.spacing['24px']} ${theme.spacing['16px']}` : '',
  maxHeight: isMobileSized ? Dimensions.get('window').height * 0.9 : undefined,
  justifyContent: 'space-between',
}))

const CoverageContainer = styled.View(({}) => ({
  width: 'auto',
  paddingBottom: '16px',
}))

const CoverageContentRow = styled.View(({}) => ({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'top-center',
  paddingVertical: '16px',
}))

const CoverageContentText = styled.View(({}) => ({
  width: '80%',
  flexDirection: 'column',
  marginLeft: '12px',
}))

const CoverageContentHeader = styled.View<{ isMobileSized: boolean }>(({ isMobileSized }) => ({
  flexDirection: 'row',
  ...(!isMobileSized && { alignItems: 'baseline' }),
  flexWrap: 'wrap',
  gap: '8px',
  paddingBottom: 4,
}))

const TitleText = styled(Subhead)<{ theme: ThemeType; hasSubhead: boolean }>(({ theme, hasSubhead }) => ({
  marginBottom: hasSubhead
    ? theme.spacing['8px']
    : theme.breakpoints.isMobileSized
    ? theme.spacing['12px']
    : theme.spacing['16px'],
}))

const SubheadText = styled(BodyText)<{ theme: ThemeType }>(({ theme }) => ({
  marginBottom: theme.breakpoints.isMobileSized ? theme.spacing['12px'] : theme.spacing['16px'],
}))

const ButtonContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  alignSelf: 'flex-end',
  width: theme.breakpoints.isMobileSized ? '100%' : 'auto',
  marginBottom: theme.breakpoints.isMobileSized ? theme.spacing['8px'] : 0,
  flexDirection: theme.breakpoints.isMobileSized ? 'column-reverse' : 'row',
  gap: theme.spacing['16px'],
}))

export interface CoverageContentProps {
  title: React.ReactNode
  description: React.ReactNode
}

const CloseIconContainer = styled(PressableOpacity)({
  alignSelf: 'flex-end',
})

const hasHPIDescription = (costMessagingModalCoverageInfo?: CostMessagingModalCoverageInfo) => {
  return (
    costMessagingModalCoverageInfo && costMessagingModalCoverageInfo?.customerCoverageType === CoverageType.HPI_ONLY
  )
}

const programsWithNoCostBadge: CostMessagingContentType[] = [
  ProgramNames.BlendedCareTherapy,
  ProgramNames.Coaching,
  ProgramNames.CoachingForParents,
  'TherapyForChildren',
  EAP_MEDS_CONTENT_TYPE,
]

const programsWithCostSharingBadge: CostMessagingContentType[] = [
  ProgramNames.MedicationManagement,
  'TherapyOrCoaching',
]

const getShouldDisplayNoCostBadge = (contentType: CostMessagingContentType, hpiOnly: boolean) => {
  return !hpiOnly && programsWithNoCostBadge.includes(contentType)
}

const getShouldDisplayCostSharingBadge = (
  contentType: CostMessagingContentType,
  hpiOnly: boolean,
  isAmazon: boolean,
  isAmazonCostTransparencyEnabled: boolean,
) => {
  return !hpiOnly && isAmazon && isAmazonCostTransparencyEnabled && programsWithCostSharingBadge.includes(contentType)
}

export const CoverageMessagingModal: FunctionComponent<CoverageMessagingModalProps> = ({
  setIsModalOpen,
  isModalOpen,
  coverageModalTitleText,
  coverageModalContents,
  costMessagingModalCoverageInfo,
  trackCoverageModalPress,
  customerName,
  numSessions,
  numberOfSessionByProgram,
  displayVisitsPerIssuePerYear,
  displayVisitsPerIssuePerYearByProgram,
  subheaderText,
  goToSearchForCare,
}) => {
  const { formatMessage } = useIntl()

  const { isMobileSized } = useMediaQuerySize()
  const { colors } = useTheme() as ThemeType
  const { isCustomerDynamicSessionLimitEnabled, isAmazonCostTransparencyEnabled } = useContext(AppContext)

  // customerName is the display name of the customer
  const isAmazon = customerName?.toLowerCase() === CustomerName.AMAZON

  const bottomSheetRef = useRef<BottomSheetGorhom>(null)
  const hasCustomerName = !isEmpty(customerName)

  let therapySessions: string | number | undefined
  let coachingSessions: string | number | undefined

  // If displayVisitsPerIssuePerYearByProgram present, prioritize per-issue-per-year by program
  if (displayVisitsPerIssuePerYearByProgram) {
    therapySessions = displayVisitsPerIssuePerYearByProgram.therapy
    coachingSessions = displayVisitsPerIssuePerYearByProgram.coaching
  } else if (numberOfSessionByProgram) {
    therapySessions = numberOfSessionByProgram.therapy
    coachingSessions = numberOfSessionByProgram.coaching
  } else if (displayVisitsPerIssuePerYear) {
    therapySessions = displayVisitsPerIssuePerYear
    coachingSessions = displayVisitsPerIssuePerYear
  } else {
    therapySessions = numSessions
    coachingSessions = numSessions
  }

  const closingModal = () => {
    trackCoverageModalPress(false)
    setIsModalOpen(false)
  }

  const openBottomSheet = () => {
    bottomSheetRef.current?.expand()
  }

  const closeBottomSheet = () => {
    closingModal()
    bottomSheetRef.current?.close()
  }

  const hpiOnly = !!hasHPIDescription(costMessagingModalCoverageInfo)

  const renderActivityBadges = useCallback(
    (contentType: CostMessagingContentType): React.ReactNode => {
      const shouldDisplayNoCostBadge = getShouldDisplayNoCostBadge(contentType, hpiOnly)
      const shouldDisplayCostSharingBadge = getShouldDisplayCostSharingBadge(
        contentType,
        hpiOnly,
        isAmazon,
        isAmazonCostTransparencyEnabled ?? false,
      )

      return (
        <>
          {shouldDisplayNoCostBadge && (
            <ActivityBadge
              text={
                <BodyText
                  text={formatMessage({
                    defaultMessage: 'No cost',
                    description: 'Cost messaging modal badge to indicate therapy or coaching sessions are covered',
                  })}
                  size={BodyTextSize.BADGE}
                  color={colors.components.badge.infoPrimary.text}
                />
              }
              variant={BadgeVariant.INFO_PRIMARY}
              shape={BadgeShape.ROUND}
              preserveCase={true}
              testID={tID(`CoverageMessagingModal-no-cost-badge-${contentType}`)}
            />
          )}
          {shouldDisplayCostSharingBadge && (
            <ActivityBadge
              text={
                <BodyText
                  text={formatMessage({
                    defaultMessage: 'Cost sharing',
                    description: 'Cost messaging modal badge to indicate a given program is eligible for cost sharing',
                  })}
                  size={BodyTextSize.BADGE}
                  color={colors.textSuccessAlternate}
                />
              }
              variant={BadgeVariant.SUCCESS}
              shape={BadgeShape.ROUND}
              preserveCase={true}
              testID={tID(`CoverageMessagingModal-cost-sharing-badge-${contentType}`)}
            />
          )}
        </>
      )
    },
    [formatMessage, colors, hpiOnly, isAmazon, isAmazonCostTransparencyEnabled],
  )

  const getCoverageIcon = (contentType: CostMessagingContentType) => {
    switch (contentType) {
      case ProgramNames.CoachingForParents:
        return coachingSessions &&
          coachingSessions !== 0 &&
          !isUnlimitedSessionLimit(Number(coachingSessions)) &&
          !isCustomerDynamicSessionLimitEnabled ? (
          <IllustrationBadge
            text={<BodyText size={BodyTextSize.SMALL} text={coachingSessions} />}
            topOffset={40}
            leftOffset={40}
            icon={<CoachingSecondaryRec width={60} height={60} />}
          />
        ) : (
          <CoachingSecondaryRec width={60} height={60} />
        )
      case 'TherapyForChildren':
        return therapySessions && therapySessions !== '0' && !isCustomerDynamicSessionLimitEnabled ? (
          <IllustrationBadge
            text={<BodyText size={BodyTextSize.SMALL} text={therapySessions} />}
            topOffset={40}
            leftOffset={40}
            icon={<ChildTherapySecondaryRec width={60} height={60} />}
          />
        ) : (
          <ChildTherapySecondaryRec width={60} height={60} />
        )
      case 'TherapyOrCoaching':
        return <TherapySecondaryRec width={60} height={60} />
      case 'ThatchTherapy':
        return therapySessions && therapySessions !== '0' && !isCustomerDynamicSessionLimitEnabled ? (
          <IllustrationBadge
            text={<BodyText size={BodyTextSize.SMALL} text={therapySessions} />}
            topOffset={40}
            leftOffset={40}
            icon={<TherapySecondaryRec width={60} height={60} />}
          />
        ) : (
          <TherapySecondaryRec width={60} height={60} />
        )
      case ProgramNames.BlendedCareTherapy:
        return therapySessions && therapySessions !== '0' && !isCustomerDynamicSessionLimitEnabled ? (
          <IllustrationBadge
            text={<BodyText size={BodyTextSize.SMALL} text={therapySessions} />}
            topOffset={40}
            leftOffset={40}
            icon={<TherapySecondaryRec width={60} height={60} />}
          />
        ) : (
          <TherapySecondaryRec width={60} height={60} />
        )
      case ProgramNames.Coaching:
        return coachingSessions &&
          coachingSessions !== 0 &&
          !isUnlimitedSessionLimit(Number(coachingSessions)) &&
          !isCustomerDynamicSessionLimitEnabled ? (
          <IllustrationBadge
            text={<BodyText size={BodyTextSize.SMALL} text={coachingSessions} />}
            topOffset={40}
            leftOffset={40}
            icon={<CoachingSecondaryRec width={60} height={60} />}
          />
        ) : (
          <CoachingSecondaryRec width={60} height={60} />
        )
      case ProgramNames.MedicationManagement:
      case EAP_MEDS_CONTENT_TYPE:
        return <ControlledMedsSecondaryRec width={60} height={60} />
      case ProgramNames.ClinicalLeaveEvaluation:
        return <ProfileBookingShortTermDisabilityEvaluationIllustration size={60} />
      default:
        return null
    }
  }

  return (
    <Modal
      modalContents={
        <ContentContainer testID={tID('CoverageMessagingModal')} isMobileSized={isMobileSized}>
          <View>
            <CloseIconContainer testID={tID('CoverageMessagingModal-closeIcon')} onPress={closeBottomSheet}>
              <CloseIcon size={16} fillColor={colors.iconDefault} />
            </CloseIconContainer>
            <TitleText
              text={
                coverageModalTitleText ?? (
                  <FormattedMessage
                    defaultMessage='Annual Lyra benefits {hasCustomerName, select, true {for {customerName}} other {}}'
                    description='Header text for coverage modal'
                    values={{ hasCustomerName, customerName }}
                  />
                )
              }
              hasSubhead={!!subheaderText}
              size={SubheadSize.MEDIUM}
            />
            {subheaderText && (
              <SubheadText size={BodyTextSize.SMALL} text={subheaderText} color={colors.textSecondary} />
            )}
            {coverageModalContents && coverageModalContents.length > 0 && (
              <CoverageContainer testID={tID('CoverageMessagingModal-contentContainer')}>
                {coverageModalContents.map((content: CostMessagingModalContent, index: number) => {
                  const { contentType, title } = content
                  const icon = getCoverageIcon(contentType)

                  return (
                    <CoverageContentRow key={`coverageContent-${index}`}>
                      <View>{icon}</View>
                      <CoverageContentText>
                        <CoverageContentHeader isMobileSized={isMobileSized}>
                          <BodyText
                            size={BodyTextSize.DEFAULT}
                            color={colors.textPrimary}
                            text={title}
                            // Keep the activity badge on separate line for mobile
                            style={isMobileSized ? { flexBasis: '100%' } : {}}
                          />
                          {renderActivityBadges(contentType)}
                        </CoverageContentHeader>
                        <BodyText
                          size={BodyTextSize.SMALL}
                          color={colors.textSecondary}
                          wrap
                          text={content.description}
                        />
                      </CoverageContentText>
                    </CoverageContentRow>
                  )
                })}
              </CoverageContainer>
            )}
          </View>

          <ButtonContainer>
            {goToSearchForCare && (
              <TertiaryButton
                onPress={() => {
                  goToSearchForCare()
                  closingModal()
                }}
                text={<FormattedMessage defaultMessage='Find care' description='Cost messaging modal button text' />}
                testID={tID('CoverageMessagingModal-searchForCareButton')}
                fullWidth={isMobileSized}
                customTextColor={colors.textActive}
              />
            )}
            <PrimaryButton
              onPress={() => {
                closingModal()
              }}
              text={<FormattedMessage defaultMessage='Got it' description='Cost messaging modal button text' />}
              testID={tID('CoverageMessagingModal-confirmationButton')}
              fullWidth={isMobileSized}
            />
          </ButtonContainer>
        </ContentContainer>
      }
      onRequestClose={closeBottomSheet}
      visible={isModalOpen}
      bottomSheetRef={bottomSheetRef}
      onCloseEnd={closeBottomSheet}
      disableBottomSheet={!isMobileSized}
      onLayout={openBottomSheet}
      scrollable={isMobileSized}
      useDynamicHeightForBottomSheet={isMobileSized}
    />
  )
}
